import React from 'react';
import { Button } from '@voilajsx/uikit/button';
import { Search, Filter, X, Image, Video } from 'lucide-react';

interface GalleryFilterProps {
  filters: {
    fileType?: 'image' | 'video';
    search?: string;
    isActive?: boolean;
  };
  setFilters: (filters: any) => void;
  showAdminFilters?: boolean;
}

const GalleryFilter: React.FC<GalleryFilterProps> = ({ 
  filters, 
  setFilters, 
  showAdminFilters = false 
}) => {
  // FIX: Allow undefined values
  const handleFilterChange = (key: string, value: string | boolean | undefined) => {
    setFilters({
      ...filters,
      [key]: value === '' || value === 'all' ? undefined : value
    });
  };

  const clearFilters = () => {
    setFilters({});
  };

  const hasActiveFilters = filters.fileType || filters.search || (showAdminFilters && filters.isActive !== undefined);

  return (
    <div className="bg-gray-50 rounded-lg p-4 space-y-4">
      <div className="flex items-center gap-2 mb-3">
        <Filter className="h-4 w-4 text-gray-600" />
        <h3 className="font-medium text-gray-700">Filter Gallery</h3>
        {hasActiveFilters && (
          <Button
            onClick={clearFilters}
            size="sm"
            variant="outline"
            className="ml-auto text-xs"
          >
            <X className="h-3 w-3 mr-1" />
            Clear All
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search gallery..."
            value={filters.search || ''}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          />
        </div>

        {/* File Type Filter */}
        <select
          value={filters.fileType || ''}
          onChange={(e) => handleFilterChange('fileType', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
        >
          <option value="">All Types</option>
          <option value="image">Images</option>
          <option value="video">Videos</option>
        </select>

        {/* Admin Status Filter */}
        {showAdminFilters && (
          <select
            value={filters.isActive === undefined ? '' : filters.isActive.toString()}
            onChange={(e) => handleFilterChange('isActive', e.target.value === '' ? undefined : e.target.value === 'true')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        )}
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-200">
          <span className="text-xs text-gray-600">Active filters:</span>
          {filters.fileType && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
              {filters.fileType === 'image' ? <Image className="h-3 w-3" /> : <Video className="h-3 w-3" />}
              {filters.fileType === 'image' ? 'Images' : 'Videos'}
              <button onClick={() => handleFilterChange('fileType', '')}>
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {filters.search && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
              Search: "{filters.search}"
              <button onClick={() => handleFilterChange('search', '')}>
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {showAdminFilters && filters.isActive !== undefined && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
              Status: {filters.isActive ? 'Active' : 'Inactive'}
              {/* FIX: Now this works because handleFilterChange accepts undefined */}
              <button onClick={() => handleFilterChange('isActive', undefined)}>
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export { GalleryFilter };
